import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import {
  WarningAction,
  WarningDisposition,
  WarningType,
  SubmissionPackageExceptionType,
  assertNever,
  resolvePreflightWarningBody,
  PreflightCopy,
  type PreflightWarning,
  type WarningDispositionInput,
} from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { formatAmount, formatDate } from '../utils';
import { PreflightContactName } from './PreflightContactName';
import { PortalActionList } from './PortalActionList';

interface HeadsUpCardProps {
  warnings: readonly PreflightWarning[];
  /** Caller-owned disposition map keyed by warningId. Heads-up card is
   *  controlled; the parent persists the selection until file-submit. */
  dispositions: Record<string, WarningDispositionInput>;
  onChange: (next: Record<string, WarningDispositionInput>) => void;
  /** Optional resolution callbacks per warning type — invoked when the
   *  treasurer clicks [Resolve]. Default = no-op (the parent may wire
   *  workspace deep-links for specific types). */
  onResolve?: (warning: PreflightWarning) => void;
  /**
   * FPD-021 — invoked when the treasurer clicks [Mark as written off] on an
   * `AgedOverdueUnsettled` warning. Receives the warning's first affected
   * contribution id; parent owns the modal (the modal uses
   * `useManualVoidPrefill` + `useWriteOffContribution` to drive the atomic
   * write-off). Button is hidden when this prop is omitted.
   */
  onMarkAsWrittenOff?: (warning: PreflightWarning, contributionId: string) => void;
}

/**
 * FPD-005 + FPD-020 — Heads-up card renders the preflight `warnings` list
 * above the Confirm button per ADR-007 § Operating principle #1 ("alert,
 * don't block"). Each row exposes its `availableActions` as button(s).
 *
 * Treasurer-visible copy comes from the FPD-020 `PreflightCopy` bundle
 * (mirrors `Features.Finance.Filing.Preflight.PreflightCopy`). The wire-locked
 * `PreflightWarning.warningText` is preserved as a fallback for forward
 * compatibility but never rendered when a bundle entry exists.
 *
 * Severity is implicit in `availableActions`: Dismiss-only = informational,
 * Attest = needs justification, Resolve = has a concrete fix path.
 */
export function HeadsUpCard({
  warnings,
  dispositions,
  onChange,
  onResolve,
  onMarkAsWrittenOff,
}: HeadsUpCardProps) {
  if (warnings.length === 0) return null;

  // §44 — split mandatory post-upload portal handoffs from soft advisories so a
  // required action never looks identical to a "nice to know". ADR-025 — membership
  // is a per-warning WIRE FLAG the server sets (`portalActionRequired`), so this and
  // the FilePreflightModal auto-dismiss guard read the SAME flag and can't drift.
  const portalActions = warnings.filter((w) => w.portalActionRequired ?? false);
  const advisories = warnings.filter((w) => !(w.portalActionRequired ?? false));

  const renderRow = (w: PreflightWarning) => (
    <HeadsUpRow
      key={w.warningId}
      warning={w}
      disposition={dispositions[w.warningId]}
      onChange={(next) => {
        const updated = { ...dispositions };
        if (next == null) delete updated[w.warningId];
        else updated[w.warningId] = next;
        onChange(updated);
      }}
      onResolve={onResolve}
      onMarkAsWrittenOff={onMarkAsWrittenOff}
    />
  );

  return (
    <div
      className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm"
      data-testid="preflight-heads-up-card"
      role="alert"
    >
      <div className="flex items-center gap-1 font-medium text-amber-900">
        <AlertTriangle className="h-4 w-4" />
        Heads up — {warnings.length} item{warnings.length === 1 ? '' : 's'} to review
      </div>

      {/* Mandatory portal handoffs — pinned first, visually stronger than the
          advisories so the treasurer can't mistake a required action for a nudge. */}
      <PortalActionList warnings={warnings} renderItem={renderRow} />

      {/* Soft advisories. The "Advisories" heading only appears when there's a
          portal-action group to distinguish it from — otherwise it's just the
          flat list the treasurer already knows. */}
      {advisories.length > 0 && (
        <div data-testid="heads-up-advisories-group" className="space-y-2">
          {portalActions.length > 0 && (
            <div
              className="text-xs font-semibold uppercase tracking-wide text-amber-800"
              data-testid="heads-up-advisories-label"
            >
              Advisories
            </div>
          )}
          <ul className="space-y-2">{advisories.map(renderRow)}</ul>
        </div>
      )}
    </div>
  );
}

interface HeadsUpRowProps {
  warning: PreflightWarning;
  disposition: WarningDispositionInput | undefined;
  onChange: (next: WarningDispositionInput | null) => void;
  onResolve?: (warning: PreflightWarning) => void;
  onMarkAsWrittenOff?: (warning: PreflightWarning, contributionId: string) => void;
}

function HeadsUpRow({
  warning,
  disposition,
  onChange,
  onResolve,
  onMarkAsWrittenOff,
}: HeadsUpRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [reason, setReason] = useState(disposition?.reason ?? '');
  const [expectedResolutionBy, setExpectedResolutionBy] = useState(
    disposition?.expectedResolutionBy ?? '',
  );

  const primaryRowId = warning.affectedRowIds[0];
  // AFU-009 — enriched display rows the server stamps on the warning
  // (id/date/amount/contactName). Today only AgedOverdueUnsettled carries
  // them; absent for every other warning type.
  const affectedRows = warning.affectedRows ?? [];

  // ADR-025 — body copy from the escape-hatch registry, keyed by code. It returns
  // null for a pack-declared code this bundle doesn't recognize, so the DEFAULT is
  // the generic tile rendered from the server-shipped `warning.warningText`.
  const bodyCopy = resolvePreflightWarningBody(
    warning.warningType,
    warning.affectedRowIds.length,
  ) ?? warning.warningText;

  // ADR-025 — the classifier label shown above the inline Attest panel is
  // SERVER-resolved and shipped on the warning. Null when the warning carries no
  // classifier (informational) or no display label.
  const classifierLabel = warning.classifier?.label ?? null;

  const canAttest = warning.availableActions.includes(WarningAction.Attest);
  const canDismiss = warning.availableActions.includes(WarningAction.Dismiss);
  const canResolve = warning.availableActions.includes(WarningAction.Resolve);
  // ADR-025 — the "mark as written off" affordance rides the WriteOff action
  // member (severity-and-affordances-are-actions-presence), not an
  // AgedOverdueUnsettled type-identity compare. Still gated on the parent wiring
  // the callback + there being an affected contribution to write off.
  const canMarkAsWrittenOff =
    warning.availableActions.includes(WarningAction.WriteOff)
      && typeof onMarkAsWrittenOff === 'function'
      && warning.affectedRowIds.length > 0;

  function handleAttest() {
    setExpanded(true);
  }
  function handleCancelAttest() {
    setExpanded(false);
    onChange(null);
  }
  function handleSaveAttest() {
    if (!reason.trim()) return;
    onChange({
      warningId: warning.warningId,
      warningType: warning.warningType,
      warningTextShown: warning.warningText,
      disposition: WarningDisposition.Attested,
      reason: reason.trim(),
      expectedResolutionBy: expectedResolutionBy || null,
      exceptionTypeClassifier: warning.classifier?.code ?? null,
      contributionId: warning.classifier?.contributionId ?? null,
    });
  }
  function handleDismiss() {
    onChange({
      warningId: warning.warningId,
      warningType: warning.warningType,
      warningTextShown: warning.warningText,
      disposition: WarningDisposition.Dismissed,
      reason: null,
      expectedResolutionBy: null,
      exceptionTypeClassifier: null,
      contributionId: null,
    });
  }

  const status = disposition?.disposition;
  return (
    <li
      className="rounded border border-amber-200 bg-white p-2"
      data-testid={`heads-up-row-${warning.warningType}`}
      data-warning-id={warning.warningId}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs text-amber-950">{bodyCopy}</p>
        {status === WarningDisposition.Attested && (
          <span
            className="rounded bg-emerald-100 px-1 text-[11px] font-medium text-emerald-900"
            data-testid="heads-up-attested-badge"
          >
            Attested
          </span>
        )}
        {status === WarningDisposition.Dismissed && (
          <span
            className="rounded bg-slate-100 px-1 text-[11px] font-medium text-slate-700"
            data-testid="heads-up-dismissed-badge"
          >
            Dismissed
          </span>
        )}
      </div>
      {/* AFU-009 — named list of the warning's aging records (name · date ·
          amount), straight off the enriched warning rows the server stamps.
          The treasurer sees WHICH records are aging, not opaque ids. Renders
          only when the warning carries enriched rows. */}
      {affectedRows.length > 0 && (
        <ul
          className="mt-1 space-y-0.5"
          data-testid={`heads-up-affected-rows-${warning.warningType}`}
        >
          {affectedRows.map((row) => (
            <li
              key={row.id}
              className="text-xs text-amber-900"
              data-testid={`heads-up-affected-row-${row.id}`}
            >
              {row.contactName ? (
                <PreflightContactName name={row.contactName} contactId={row.contactId} />
              ) : (
                '(unknown contact)'
              )}{' '}
              · {formatDate(row.date)} ·{' '}
              {formatAmount(row.amount)}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        {canResolve && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onResolve?.(warning)}
            data-testid="heads-up-resolve"
          >
            Resolve
          </Button>
        )}
        {canMarkAsWrittenOff && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onMarkAsWrittenOff?.(warning, primaryRowId!)}
            data-testid="heads-up-mark-written-off"
          >
            {PreflightCopy.MarkAsWrittenOffButtonLabel}
          </Button>
        )}
        {canAttest && !expanded && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleAttest}
            data-testid="heads-up-attest"
          >
            {PreflightCopy.AttestButtonLabel}
            <ChevronDown className="ml-1 h-3 w-3" />
          </Button>
        )}
        {canDismiss && status !== WarningDisposition.Dismissed && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            data-testid="heads-up-dismiss"
          >
            {PreflightCopy.DismissNotApplicableLabel}
          </Button>
        )}
        {status != null && !expanded && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onChange(null)}
            data-testid="heads-up-clear"
          >
            Clear choice
          </Button>
        )}
      </div>
      {/* §30 — clarify what "Not applicable" does NOT do (it is not a record that
          the portal step was completed), so it can't be mistaken for "handled". */}
      {canDismiss && status !== WarningDisposition.Dismissed && (
        <p className="mt-1 text-xs text-amber-800" data-testid="heads-up-dismiss-help">
          {PreflightCopy.DismissNotApplicableHelp}
        </p>
      )}

      {expanded && (
        <div className="mt-2 space-y-2 border-t border-amber-200 pt-2">
          {classifierLabel && (
            <p
              className="text-xs font-medium text-amber-900"
              data-testid="heads-up-classifier-label"
            >
              {classifierLabel}
            </p>
          )}
          <div className="space-y-1">
            <label className="block text-xs font-medium text-amber-900" htmlFor={`reason-${warning.warningId}`}>
              Reason (required)
            </label>
            <textarea
              id={`reason-${warning.warningId}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-amber-300 bg-white px-2 py-1 text-xs"
              data-testid="heads-up-reason"
              placeholder="Explain why you are filing despite this warning"
            />
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium text-amber-900" htmlFor={`erb-${warning.warningId}`}>
              Expected resolution by (optional)
            </label>
            <input
              id={`erb-${warning.warningId}`}
              type="date"
              value={expectedResolutionBy}
              onChange={(e) => setExpectedResolutionBy(e.target.value)}
              className="rounded-md border border-amber-300 bg-white px-2 py-1 text-xs"
              data-testid="heads-up-erb"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={handleCancelAttest}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSaveAttest}
              disabled={!reason.trim()}
              data-testid="heads-up-save-attest"
            >
              Save attestation
              <ChevronUp className="ml-1 h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

// FPD-020 retired the inline classifier dropdown that let the treasurer
// pick between ManualEntryPreDeposit and DonorIdentityUnresolved on a
// PendingInPeriod warning. Per-row inference (`isManualEntry`) drives the
// classifier choice now; DIU is hard-blocked upstream via
// `MissingContactNameParts`.
//
// Re-exports preserved for backward-compat with existing imports.
export { SubmissionPackageExceptionType, WarningType, assertNever };
